/**
 * @fileoverview Plugin per la configurazione globale di Element UI
 *
 * Questo plugin configura Element UI con localizzazione italiana e tema personalizzato
 * per tutta l'applicazione Vue. Include variabili SCSS personalizzate e configurazioni
 * ottimizzate per l'interfaccia utente del progetto timecard.
 *
 * @author Generated by TypeScript Conversion
 * @since 2025-09-23
 */

import Vue from 'vue';
import Element from 'element-ui';
import locale from 'element-ui/lib/locale/lang/it';

// Import del tema personalizzato con variabili SCSS
import '@/assets/css/_element-variables.scss';

// Fix per il locale italiano: rimuove "anno" problematico
const fixedLocale = {
	...locale,
	el: {
		...locale.el,
		datepicker: {
			...locale.el.datepicker,
			year: '', // Rimuove la parola "anno" che causa "2025 anno"
		},
	},
};

/**
 * Configurazione globale per Element UI
 *
 * Definisce le opzioni utilizzate per configurare Element UI
 * in modo consistente in tutta l'applicazione.
 */
interface ElementUIConfig {
	/** Dimensione predefinita per tutti i componenti Element UI */
	size: 'large' | 'medium' | 'small' | 'mini';
	/** Localizzazione/lingua per Element UI */
	locale: any; // Tipo flessibile per compatibilità con Element UI
}

/**
 * Configurazione predefinita di Element UI per il progetto
 *
 * @constant
 * @type {ElementUIConfig}
 */
const ELEMENT_UI_CONFIG: ElementUIConfig = {
	/**
	 * Dimensione 'small' per un'interfaccia più compatta
	 *
	 * Opzioni disponibili:
	 * - 'large': Componenti più grandi (raramente usato)
	 * - 'medium': Dimensione standard (default Element UI)
	 * - 'small': Più compatto, ideale per applicazioni business ✅
	 * - 'mini': Molto piccolo (per spazi limitati)
	 */
	size: 'small',

	/**
	 * Localizzazione italiana per tutti i componenti (con fix)
	 *
	 * Include traduzioni per:
	 * - Date picker (nomi mesi, giorni) - FIXED: rimosso "anno" problematico
	 * - Pagination (testo "elementi per pagina", etc.)
	 * - Dialog (pulsanti "OK", "Annulla")
	 * - Upload (messaggi di stato)
	 * - Form validation (messaggi di errore)
	 */
	locale: fixedLocale,
} as const;

/**
 * Plugin Nuxt per la configurazione di Element UI
 *
 * Configura Element UI globalmente con le impostazioni ottimali per
 * l'applicazione timecard, inclusi tema personalizzato e localizzazione italiana.
 *
 * **Caratteristiche principali:**
 * - 🎨 **Tema personalizzato** via variabili SCSS
 * - 🇮🇹 **Localizzazione italiana** completa
 * - 📏 **Dimensione compatta** per UI business
 * - 🎯 **Configurazione centralizzata** e type-safe
 *
 * **Componenti disponibili globalmente dopo l'installazione:**
 * - Tutti i componenti Element UI (el-button, el-input, el-table, etc.)
 * - Message e MessageBox ($message, $msgbox, $alert, $confirm, $prompt)
 * - Notification ($notify)
 * - Loading ($loading)
 *
 * @example
 * ### Utilizzo Componenti nei Template
 * ```vue
 * <template>
 *   <div>
 *     <!-- Componenti Element UI disponibili globalmente -->
 *     <el-button type="primary" size="small">Salva</el-button>
 *     <el-input v-model="value" placeholder="Inserisci testo"></el-input>
 *
 *     <!-- Date picker con localizzazione italiana -->
 *     <el-date-picker
 *       v-model="date"
 *       type="date"
 *       placeholder="Seleziona data">
 *     </el-date-picker>
 *
 *     <!-- Tabella con dimensione predefinita 'small' -->
 *     <el-table :data="tableData" size="small">
 *       <el-table-column prop="name" label="Nome"></el-table-column>
 *     </el-table>
 *   </div>
 * </template>
 * ```
 *
 * @example
 * ### Utilizzo Servizi Programmatici
 * ```typescript
 * // Message (toast)
 * this.$message.success('Operazione completata!');
 * this.$message.error('Errore durante il salvataggio');
 *
 * // MessageBox (dialog)
 * this.$confirm('Confermare l\'eliminazione?', 'Attenzione', {
 *   confirmButtonText: 'OK',
 *   cancelButtonText: 'Annulla',
 *   type: 'warning'
 * }).then(() => {
 *   // Azione confermata
 * });
 *
 * // Notification
 * this.$notify({
 *   title: 'Successo',
 *   message: 'Dati salvati correttamente',
 *   type: 'success'
 * });
 *
 * // Loading
 * const loading = this.$loading({
 *   lock: true,
 *   text: 'Caricamento in corso...',
 *   spinner: 'el-icon-loading',
 *   background: 'rgba(0, 0, 0, 0.7)'
 * });
 * // ... operazione asincrona
 * loading.close();
 * ```
 *
 * @example
 * ### Personalizzazione Tema SCSS
 * ```scss
 * // Nel file _element-variables.scss
 *
 * // Colori primari
 * $--color-primary: #409EFF;
 * $--color-success: #67C23A;
 * $--color-warning: #E6A23C;
 * $--color-danger: #F56C6C;
 *
 * // Font
 * $--font-path: '~element-ui/lib/theme-chalk/fonts';
 * $--font-size-base: 14px;
 *
 * // Border radius
 * $--border-radius-base: 4px;
 * ```
 *
 * @example
 * ### Modifica Configurazione
 * ```typescript
 * // Per cambiare la configurazione, modificare ELEMENT_UI_CONFIG:
 * const ELEMENT_UI_CONFIG: ElementUIConfig = {
 *   size: 'medium',  // Cambia dimensione predefinita
 *   locale: locale   // Mantieni localizzazione italiana
 * };
 * ```
 */
export default function elementUIPlugin(): void {
	try {
		// Installazione di Element UI con configurazione personalizzata
		Vue.use(Element, ELEMENT_UI_CONFIG as any);
	} catch (error) {
		console.error('[ElementUI] Failed to initialize Element UI:', error);
		throw new Error(`Element UI initialization failed: ${error}`);
	}
}

/**
 * Esporta tipi per uso esterno
 */
export type { ElementUIConfig };

/**
 * Esporta configurazione per riferimento esterno
 */
export { ELEMENT_UI_CONFIG };

/**
 * Nota: I tipi per i servizi Element UI ($message, $confirm, etc.) sono
 * già definiti dalla libreria Element UI stessa, quindi non è necessario
 * ridefinirli qui. Il supporto TypeScript è incluso automaticamente.
 */
